import React, { useState, useMemo } from 'react';
import { Button, Grid, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, FormControlLabel, RadioGroup, Radio, FormControl, FormLabel } from "@mui/material";
import CurrencyInput from "../../../../common/CurrencyInput/CurrencyInput";
import { refundableBookingTransactionStatuses } from "../../../../constants/securspace-constants";
import Busy from "../../../../Busy";
import request from '../../../../../util/SuperagentUtils';
import { CallbackActions } from "../../../../../util/ActionUtils";
import { getErrorMessageForStandardResponse } from "../../../../../util/NetworkErrorUtil";

interface Invoice {
  transactionId: string;
  transactionNumber?: string | number;
  status?: string;
  supplierPayoutOnHold?: boolean;
}

interface CreateRequestRefundModalProps {
  invoice: Invoice;
  open: boolean;
  onInvoiceAction: (callbackActions: any, invoice?: any, message?: string) => void;
  onClose: (callbackActions?: any, invoice?: Invoice, message?: string) => void;
}

const CreateRequestRefundModal: React.FC<CreateRequestRefundModalProps> = (props) => {
  const { invoice, open, onInvoiceAction, onClose } = props;
  const [fullRefund, setFullRefund] = useState<boolean>(true);
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [removePayoutHold, setRemovePayoutHold] = useState<string>('');

  const showPayoutHoldPrompt = useMemo(() => {
    return !fullRefund && Boolean(invoice?.supplierPayoutOnHold);
  }, [fullRefund, invoice?.supplierPayoutOnHold]);

  const requestTypeLabels = useMemo(() => {
    return invoice?.status && refundableBookingTransactionStatuses.includes(invoice.status as any) ? {
      title: "Create a Refund For This Charge",
      submit: "Create a Refund",
      success: "Successfully created refund! Refund Invoice: "
    } : {
      title: "Request a Refund For This Charge",
      submit: "Request a Refund",
      success: "Successfully submitted refund request! Refund Invoice: "
    };
  }, [invoice?.status]);

  const handleClose = () => {
    onClose();
  };

  const onFullRefundChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFullRefund(event.target.checked);
    setRemovePayoutHold('');
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const reasonForRefund = formData.get('reason') as string;

    const url = "api/booking/refund-transaction";
    const payload: Record<string, any> = {
      transactionNumberToRefund: invoice?.transactionNumber,
      refundAmount: fullRefund ? null : refundAmount,
      reasonForRefund
    };

    // For full refunds, the API automatically removes the hold.
    // For partial refunds, pass the admin's selection if the invoice has a payout hold.
    if (!fullRefund && invoice?.supplierPayoutOnHold && removePayoutHold !== '') {
      payload.removeSupplierPayoutHold = removePayoutHold === 'yes';
    }

    Busy.set(true);
    request.post(url).send(payload).then(() => {
      onClose(CallbackActions.SUCCESS, invoice, requestTypeLabels.success + invoice?.transactionNumber);
    }).catch((error: any) => {
      onInvoiceAction(CallbackActions.ERROR, invoice, getErrorMessageForStandardResponse(error, ""));
    }).finally(() => {
      Busy.set(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit
      }}
    >
      <DialogTitle>
        {requestTypeLabels.title}
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          margin="dense"
          id="outlined-multiline-static"
          name="reason"
          label="Reason for Refund"
          helperText="Enter the reason this refund is being given."
          multiline
          minRows={1}
          maxRows={10}
          fullWidth
          variant="standard"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={fullRefund}
              onChange={onFullRefundChange}
            />
          }
          label="Full Refund?" />

        {
          !fullRefund && <>
            <DialogContentText>
              Enter the refund credit amount and reason for refund
            </DialogContentText>
            <CurrencyInput
              autoFocus
              required
              margin="dense"
              name="refundAmount"
              label="Refund Amount"
              helperText="Enter the amount to refund for this transaction."
              value={refundAmount}
              onChange={setRefundAmount}
              fullWidth
              variant="standard"
              inputProps={{ maxLength: 10 }}
            />
          </>
        }
        {
          showPayoutHoldPrompt && (
            <FormControl required sx={{ mt: 2 }} fullWidth>
              <FormLabel id="payout-hold-label">
                This invoice has a supplier payout hold. Would you like to remove the hold?
              </FormLabel>
              <RadioGroup
                aria-labelledby="payout-hold-label"
                name="removePayoutHold"
                value={removePayoutHold}
                onChange={(e) => setRemovePayoutHold(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes, remove the payout hold" />
                <FormControlLabel value="no" control={<Radio />} label="No, keep the payout hold" />
              </RadioGroup>
            </FormControl>
          )
        }
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent={"center"}>
          <Button variant="text" onClick={handleClose} >Cancel</Button>
          <Button
            variant="contained"
            type="submit"
            disabled={showPayoutHoldPrompt && removePayoutHold === ''}
          >
            {requestTypeLabels.submit}
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRequestRefundModal;
